import { type PropsWithChildren } from 'react';
import { Button, createTheme, MantineProvider, NumberInput, rem, Tooltip } from '@mantine/core';
import { DatePicker, DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { DeleteModal } from '@/components/modal/DeleteModal';
import { colors } from '@/styles/colors';

const theme = createTheme({
  fontFamily: 'Outfit, sans-serif',
  fontFamilyMonospace: 'Outfit, monospace',
  headings: { fontFamily: 'Outfit, sans-serif' },
  cursorType: 'pointer',
  primaryShade: 5,
  primaryColor: 'primary',
  colors: {
    ...colors,
  },
  defaultRadius: 12,
  components: {
    Button: Button.extend({
      defaultProps: {
        size: 'md',
      },
      vars: (_, props) => {
        if (props.size === 'sm') {
          return {
            root: {
              '--button-height': rem(32),
              '--button-radius': rem(10),
            },
          };
        }
        if (props.size === 'md') {
          return {
            root: {
              '--button-height': rem(44),
              '--button-radius': rem(12),
            },
          };
        }

        return { root: {} };
      },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        events: { hover: true, focus: true, touch: true },
      },
    }),
    DatePicker: DatePicker.extend({
      defaultProps: {
        highlightToday: true,
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        thousandSeparator: '.',
        decimalSeparator: ',',
        allowDecimal: false,
      },
    }),
  },
});

const modals = {
  delete: DeleteModal,
};

declare module '@mantine/modals' {
  export interface MantineModalsOverride {
    modals: typeof modals;
  }
}

const ThemeProvider = ({ children }: PropsWithChildren) => {
  return (
    <MantineProvider theme={theme}>
      <DatesProvider settings={{ locale: 'id', firstDayOfWeek: 1 }}>
        <ModalsProvider modals={modals}>
          <Notifications />
          {children}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
};

export default ThemeProvider;
